import React, { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'
import api from '../services/api'

interface Market {
  code: string
  name: string
  flag: string
  currency: string
  region: string
}

interface ComparisonMetric {
  label: string
  values: (string | number)[]
  best: {
    code: string
    value: string | number
  }
}

interface ComparisonData {
  markets: Market[]
  metrics: Record<string, ComparisonMetric>
  rankings: Record<string, string[]>
  recommendations: string[]
  generated_at: string
}

interface ScorecardItem {
  market: { code: string; name: string; flag: string }
  scores: Record<string, number>
  overall_score: number
}

interface RecommendationItem {
  market: { code: string; name: string; flag: string; currency: string }
  score: number
  metrics: {
    revenue_potential: number
    growth_rate: number
    compliance_score: number
    entry_difficulty: string
  }
  reasons: string[]
  estimated_setup_cost: number
}

const MarketComparison: React.FC = () => {
  const [availableMarkets, setAvailableMarkets] = useState<Market[]>([])
  const [selectedMarkets, setSelectedMarkets] = useState<string[]>(['UK', 'DE', 'FR'])
  const [comparisonData, setComparisonData] = useState<ComparisonData | null>(null)
  const [scorecardData, setScorecardData] = useState<ScorecardItem[]>([])
  const [recommendations, setRecommendations] = useState<RecommendationItem[]>([])
  const [activeTab, setActiveTab] = useState<'compare' | 'scorecard' | 'recommend'>('compare')
  const [loading, setLoading] = useState(false)
  const [currentMarkets, setCurrentMarkets] = useState<string[]>(['UK'])
  const [priority, setPriority] = useState<'growth' | 'revenue' | 'ease'>('growth')

  useEffect(() => {
    loadAvailableMarkets()
  }, [])

  useEffect(() => {
    if (selectedMarkets.length > 0) {
      loadComparisonData()
      loadScorecardData()
    }
  }, [selectedMarkets])

  useEffect(() => {
    loadRecommendations()
  }, [currentMarkets, priority])

  const loadAvailableMarkets = async () => {
    try {
      const response = await api.get('/api/market-comparison/markets')
      setAvailableMarkets(response.data)
    } catch (error) {
      console.error('Failed to load markets:', error)
      // Use mock data on failure
      setAvailableMarkets([
        { code: 'UK', name: 'United Kingdom', flag: '🇬🇧', currency: 'GBP', region: 'Europe' },
        { code: 'DE', name: 'Germany', flag: '🇩🇪', currency: 'EUR', region: 'Europe' },
        { code: 'FR', name: 'France', flag: '🇫🇷', currency: 'EUR', region: 'Europe' },
        { code: 'ES', name: 'Spain', flag: '🇪🇸', currency: 'EUR', region: 'Europe' },
        { code: 'IT', name: 'Italy', flag: '🇮🇹', currency: 'EUR', region: 'Europe' },
        { code: 'NL', name: 'Netherlands', flag: '🇳🇱', currency: 'EUR', region: 'Europe' },
        { code: 'BE', name: 'Belgium', flag: '🇧🇪', currency: 'EUR', region: 'Europe' },
        { code: 'PL', name: 'Poland', flag: '🇵🇱', currency: 'PLN', region: 'Europe' },
        { code: 'SE', name: 'Sweden', flag: '🇸🇪', currency: 'SEK', region: 'Europe' },
        { code: 'AT', name: 'Austria', flag: '🇦🇹', currency: 'EUR', region: 'Europe' },
      ])
    }
  }

  const loadComparisonData = async () => {
    setLoading(true)
    try {
      const response = await api.get(`/api/market-comparison/compare?markets=${selectedMarkets.join(',')}`)
      setComparisonData(response.data)
    } catch (error) {
      console.error('Failed to load comparison:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadScorecardData = async () => {
    try {
      const response = await api.get(`/api/market-comparison/scorecard?markets=${selectedMarkets.join(',')}`)
      setScorecardData(response.data.scorecards || [])
    } catch (error) {
      console.error('Failed to load scorecard:', error)
    }
  }

  const loadRecommendations = async () => {
    try {
      const response = await api.get(
        `/api/market-comparison/recommend?current_markets=${currentMarkets.join(',')}&priority=${priority}`
      )
      setRecommendations(response.data.recommendations || [])
    } catch (error) {
      console.error('Failed to load recommendations:', error)
    }
  }

  const toggleMarketSelection = (code: string) => {
    setSelectedMarkets(prev => {
      if (prev.includes(code)) {
        return prev.filter(m => m !== code)
      }
      if (prev.length >= 5) {
        return prev // Max 5 markets
      }
      return [...prev, code]
    })
  }

  const formatValue = (value: string | number, metric: string): string => {
    if (typeof value === 'string') return value
    if (metric === 'revenue') return `$${value.toLocaleString()}`
    if (metric.includes('rate') || metric.includes('share')) return `${value}%`
    if (metric === 'average_order_value') return `$${value.toFixed(2)}`
    return value.toLocaleString()
  }

  const getScoreColor = (score: number): string => {
    if (score >= 80) return '#10B981'
    if (score >= 60) return '#F59E0B'
    return '#EF4444'
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#F9FAFB' }}>
      <Header />
      <div style={{ padding: '24px', maxWidth: '1600px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <h1 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '8px' }}>
            Market Comparison Tool
          </h1>
          <p style={{ color: '#6B7280', fontSize: '14px' }}>
            Compare markets side by side to make informed expansion decisions
          </p>
        </div>

        {/* Tab Navigation */}
        <div style={{
          display: 'flex',
          gap: '8px',
          marginBottom: '24px',
          borderBottom: '2px solid #E5E7EB',
          paddingBottom: '8px'
        }}>
          {[
            { key: 'compare', label: 'Compare Markets' },
            { key: 'scorecard', label: 'Market Scorecard' },
            { key: 'recommend', label: 'Expansion Recommendations' },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key as typeof activeTab)}
              style={{
                padding: '10px 20px',
                fontSize: '14px',
                fontWeight: activeTab === tab.key ? '600' : '400',
                color: activeTab === tab.key ? '#4A90E2' : '#6B7280',
                backgroundColor: activeTab === tab.key ? '#EFF6FF' : 'transparent',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Market Selection */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '24px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <h3 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px' }}>
            Select Markets to Compare (max 5)
          </h3>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {availableMarkets.map(market => (
              <button
                key={market.code}
                onClick={() => toggleMarketSelection(market.code)}
                style={{
                  padding: '8px 16px',
                  fontSize: '13px',
                  border: selectedMarkets.includes(market.code) ? '2px solid #4A90E2' : '2px solid #E5E7EB',
                  borderRadius: '20px',
                  backgroundColor: selectedMarkets.includes(market.code) ? '#EFF6FF' : 'white',
                  color: selectedMarkets.includes(market.code) ? '#4A90E2' : '#374151',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  transition: 'all 0.2s',
                }}
              >
                <span>{market.flag}</span>
                <span>{market.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Compare Tab */}
        {activeTab === 'compare' && comparisonData && (
          <div>
            {/* Comparison Table */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '24px',
              marginBottom: '24px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              overflowX: 'auto'
            }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px' }}>
                Side-by-Side Comparison
              </h3>
              {loading ? (
                <div style={{ padding: '40px', textAlign: 'center', color: '#9CA3AF' }}>
                  Loading comparison data...
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '800px' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #E5E7EB' }}>
                      <th style={{
                        textAlign: 'left',
                        padding: '12px',
                        fontSize: '13px',
                        color: '#6B7280',
                        fontWeight: '600',
                        minWidth: '150px'
                      }}>
                        Metric
                      </th>
                      {comparisonData.markets.map(market => (
                        <th
                          key={market.code}
                          style={{
                            textAlign: 'right',
                            padding: '12px',
                            fontSize: '13px',
                            fontWeight: '600',
                            minWidth: '120px'
                          }}
                        >
                          <span>{market.flag}</span> {market.name}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(comparisonData.metrics).map(([key, metric]) => (
                      <tr key={key} style={{ borderBottom: '1px solid #F3F4F6' }}>
                        <td style={{ padding: '12px', fontSize: '14px', fontWeight: '500' }}>
                          {metric.label}
                        </td>
                        {metric.values.map((value, idx) => (
                          <td
                            key={idx}
                            style={{
                              padding: '12px',
                              fontSize: '14px',
                              textAlign: 'right',
                              fontWeight: metric.best.code === comparisonData.markets[idx]?.code ? '700' : '400',
                              color: metric.best.code === comparisonData.markets[idx]?.code ? '#10B981' : '#374151',
                            }}
                          >
                            {formatValue(value, key)}
                            {metric.best.code === comparisonData.markets[idx]?.code && (
                              <span style={{ marginLeft: '4px', fontSize: '12px' }}>✓</span>
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Recommendations */}
            {comparisonData.recommendations.length > 0 && (
              <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '24px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
              }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px' }}>
                  Key Insights
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {comparisonData.recommendations.map((rec, idx) => (
                    <div
                      key={idx}
                      style={{
                        padding: '12px 16px',
                        backgroundColor: '#F9FAFB',
                        borderRadius: '8px',
                        fontSize: '14px',
                        borderLeft: '4px solid #4A90E2'
                      }}
                    >
                      {rec}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Scorecard Tab */}
        {activeTab === 'scorecard' && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '20px'
          }}>
            {scorecardData.map(item => (
              <div
                key={item.market.code}
                style={{
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  padding: '24px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                  <span style={{ fontSize: '32px' }}>{item.market.flag}</span>
                  <div>
                    <h3 style={{ fontSize: '18px', fontWeight: '600' }}>{item.market.name}</h3>
                    <div style={{
                      fontSize: '24px',
                      fontWeight: '700',
                      color: getScoreColor(item.overall_score)
                    }}>
                      {item.overall_score}
                      <span style={{ fontSize: '14px', fontWeight: '400', color: '#6B7280' }}> / 100</span>
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {Object.entries(item.scores).map(([key, score]) => (
                    <div key={key}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: '13px',
                        marginBottom: '4px'
                      }}>
                        <span style={{ color: '#6B7280' }}>
                          {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </span>
                        <span style={{ fontWeight: '600' }}>{score}</span>
                      </div>
                      <div style={{
                        height: '8px',
                        backgroundColor: '#E5E7EB',
                        borderRadius: '4px',
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          height: '100%',
                          width: `${score}%`,
                          backgroundColor: getScoreColor(score),
                          borderRadius: '4px',
                          transition: 'width 0.5s ease'
                        }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Recommendations Tab */}
        {activeTab === 'recommend' && (
          <div>
            {/* Controls */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '20px',
              marginBottom: '24px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              display: 'flex',
              gap: '24px',
              flexWrap: 'wrap',
              alignItems: 'flex-end'
            }}>
              <div>
                <label style={{ fontSize: '13px', color: '#6B7280', display: 'block', marginBottom: '6px' }}>
                  Current Markets
                </label>
                <select
                  multiple
                  value={currentMarkets}
                  onChange={e => setCurrentMarkets(Array.from(e.target.selectedOptions, opt => opt.value))}
                  style={{
                    padding: '8px',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    minWidth: '150px',
                    height: '100px'
                  }}
                >
                  {availableMarkets.map(m => (
                    <option key={m.code} value={m.code}>{m.flag} {m.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ fontSize: '13px', color: '#6B7280', display: 'block', marginBottom: '6px' }}>
                  Priority
                </label>
                <select
                  value={priority}
                  onChange={e => setPriority(e.target.value as typeof priority)}
                  style={{
                    padding: '8px 16px',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                >
                  <option value="growth">Growth Potential</option>
                  <option value="revenue">Revenue Size</option>
                  <option value="ease">Ease of Entry</option>
                </select>
              </div>
            </div>

            {/* Recommendations List */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {recommendations.map((rec, idx) => (
                <div
                  key={rec.market.code}
                  style={{
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    padding: '24px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    display: 'flex',
                    gap: '24px',
                    alignItems: 'flex-start'
                  }}
                >
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    backgroundColor: idx === 0 ? '#FEF3C7' : idx === 1 ? '#E5E7EB' : idx === 2 ? '#FED7AA' : '#F3F4F6',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: '700',
                    fontSize: '16px',
                    color: idx === 0 ? '#D97706' : '#374151',
                    flexShrink: 0
                  }}>
                    #{idx + 1}
                  </div>

                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                      <span style={{ fontSize: '32px' }}>{rec.market.flag}</span>
                      <div>
                        <h3 style={{ fontSize: '18px', fontWeight: '600' }}>{rec.market.name}</h3>
                        <div style={{ fontSize: '14px', color: '#6B7280' }}>
                          Score: <span style={{ fontWeight: '600', color: '#4A90E2' }}>{rec.score.toFixed(1)}</span>
                        </div>
                      </div>
                    </div>

                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                      gap: '16px',
                      marginBottom: '16px'
                    }}>
                      <div>
                        <div style={{ fontSize: '12px', color: '#6B7280' }}>Revenue Potential</div>
                        <div style={{ fontSize: '16px', fontWeight: '600' }}>
                          ${rec.metrics.revenue_potential.toLocaleString()}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: '12px', color: '#6B7280' }}>Growth Rate</div>
                        <div style={{ fontSize: '16px', fontWeight: '600', color: '#10B981' }}>
                          {rec.metrics.growth_rate}%
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: '12px', color: '#6B7280' }}>Compliance Score</div>
                        <div style={{ fontSize: '16px', fontWeight: '600' }}>
                          {rec.metrics.compliance_score}/100
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: '12px', color: '#6B7280' }}>Entry Difficulty</div>
                        <div style={{
                          fontSize: '14px',
                          fontWeight: '600',
                          color: rec.metrics.entry_difficulty === 'Low' ? '#10B981' :
                                 rec.metrics.entry_difficulty === 'Medium' ? '#F59E0B' : '#EF4444'
                        }}>
                          {rec.metrics.entry_difficulty}
                        </div>
                      </div>
                    </div>

                    <div style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '8px',
                      marginBottom: '12px'
                    }}>
                      {rec.reasons.map((reason, i) => (
                        <span
                          key={i}
                          style={{
                            padding: '4px 10px',
                            backgroundColor: '#EFF6FF',
                            borderRadius: '12px',
                            fontSize: '12px',
                            color: '#4A90E2'
                          }}
                        >
                          {reason}
                        </span>
                      ))}
                    </div>

                    <div style={{
                      padding: '12px 16px',
                      backgroundColor: '#F9FAFB',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}>
                      Estimated Setup Cost: <span style={{ fontWeight: '600' }}>
                        ${rec.estimated_setup_cost.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              ))}

              {recommendations.length === 0 && (
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  padding: '40px',
                  textAlign: 'center',
                  color: '#9CA3AF'
                }}>
                  No recommendations available. Try selecting different current markets.
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default MarketComparison
